type AccountLabelParams = {
  baseLabel: string;
  resumableSurveyCount: number;
  showUpdateBadge: boolean;
};

/**
 * Spells out how many surveys are waiting, or `undefined` when none are.
 *
 * Shared by every account entry point that can wear the pending-survey dot, so
 * the dot is described the same way wherever a screen reader meets it: the
 * header avatar, the web nav avatar, and the account item inside its menu.
 */
export function describeResumableSurveys(count: number): string | undefined {
  if (count <= 0) {
    return undefined;
  }

  return `${count} ${count === 1 ? 'survey' : 'surveys'} to complete`;
}

/**
 * Builds the account button's screen-reader label, always set because the avatar has no
 * visible text. A badge adds every reason it could be there, since one dot stands for both
 * a pending survey and a ready update.
 */
export function getAccountButtonAccessibilityLabel({
  baseLabel,
  resumableSurveyCount,
  showUpdateBadge,
}: AccountLabelParams): string {
  const reasons: string[] = [];

  const surveys = describeResumableSurveys(resumableSurveyCount);

  if (surveys) {
    reasons.push(surveys);
  }

  if (showUpdateBadge) {
    reasons.push('update available');
  }

  if (reasons.length === 0) {
    return baseLabel;
  }

  return `${baseLabel}, ${reasons.join(', ')}`;
}
